import { AlertMessageProps, SecurityAnswerTooltip } from "@hqy/react-shared-components";
import { Button, Grid, Link, MenuItem, Select, TextField } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Field, Form, Formik } from "formik";
import parseHtml from "html-react-parser";
import React, { Dispatch, SetStateAction, useState } from "react";
import UserCredentialsApi from "../../services/Api";
import { stringToHex } from "../../services/CommonHelpers";
import { NetworkError, ValidationError } from "../../services/Errors";
import {
  BlockReasonTypes,
  BlockReasonTypesProperties,
  Brand,
  CredentialBlock,
  CredentialResponse,
  CredentialsStatusActions,
  credentialsStatusMap,
  FormActionStatus,
  freeFormQuestionId,
  PendingActions,
  PredefinedQuestion,
  UpdateUserCredentialsRequest,
  UserPermissions,
} from "../../services/Types";
import { sqaFormValidationSchema } from "../../services/ValidationSchemas";
import FormActionStatusIcon from "../FormActionStatusIcon/FormActionStatusIcon";
import PermissionCheck from "../PermissionCheck";

const useStyles = makeStyles({
  statusSectionHolder: {
    margin: "-3px 0 0",
    "& .MuiOutlinedInput-root": {
      margin: "0 0 10px",
    },
    "& p": {
      fontSize: "16px",
      lineHeight: "1.4em",
      margin: "5px 0",
      color: "#323232",
    },
  },
  temporaryPasswordTextField: {
    "& .MuiOutlinedInput-root": {
      color: "#000",
      backgroundColor: "#dcdcdc!important",
    },
    "& .MuiOutlinedInput-input": {
      padding: "17px 35px",
      fontSize: "20px",
      fontWeight: "600",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "0!important",
    },
  },
  actionsHolder: {
    fontSize: "16px",
    margin: "6px 0 0",
  },
  forceUpdatesHolder: {
    "& p": {
      margin: "6px 0",
      "&:first-child": {
        marginTop: "17px",
      },
      "&:last-child": {
        marginBottom: "39px",
      },
    },
  },
  blockUnblockHolder: {
    "& p": {
      margin: "6px 0",
      "&:first-child": {
        marginTop: "17px",
      },
      "&:last-child": {
        marginBottom: "39px",
      },
    },
  },
  showAnswer: {
    float: "right",
    fontSize: "16px",
  },
  generateTemporaryPassword: {
    fontSize: "24px!important",
  },
  sqaHolder: {
    margin: "22px 0 0",
  },
  securityAnswerHolder: {
    margin: "8px 0 0",
  },
  temporaryPasswordHolder: {
    margin: "9px 0 0",
  },
  temporaryPasswordWithCopyIcon: {
    position: "relative",
  },
  copyTemporaryPassword: {
    position: "absolute",
    right: "13px",
    top: "50%",
    fontSize: "25px!important",
    marginTop: "-17px",
    cursor: "pointer",
    color: "#000",
  },
  fieldErrorsHolder: {
    position: "relative",
    padding: "0 0 7px 25px",
    color: "#D80443",
    "& i": {
      position: "absolute",
      top: "0",
      left: "0",
    },
  },
});

const LoginAndSecurity = ({
  userCredentials,
  userPermissions,
  allQuestions,
  userCredentialsPendingActions,
  setUserCredentials,
  userCredentialsApi,
  setUserCredentialsPendingActions,
  handleTabDataReload,
  brand,
  activeAnswer,
  setActiveAnswer,
  selectedQuestionId,
  setSelectedQuestionId,
  setAlertMessageProps,
  errorOutlines,
  setErrorOutlines
}: {
  userCredentials: CredentialResponse;
  userPermissions: Array<UserPermissions>;
  allQuestions: Array<PredefinedQuestion>;
  userCredentialsPendingActions: PendingActions;
  setUserCredentials: Dispatch<SetStateAction<CredentialResponse | null>>;
  setUserCredentialsPendingActions: Dispatch<SetStateAction<PendingActions>>;
  handleTabDataReload: any;
  brand?: Brand;
  userCredentialsApi: UserCredentialsApi;
  activeAnswer: string;
  setActiveAnswer: Dispatch<SetStateAction<string>>;
  selectedQuestionId: string;
  setSelectedQuestionId: Dispatch<SetStateAction<string>>;
  setAlertMessageProps: Dispatch<SetStateAction<AlertMessageProps | null>>;
  errorOutlines: any;
  setErrorOutlines: Dispatch<SetStateAction<any>>;
}) => {
  const classes = useStyles();

  const [showAnswer, setShowAnswer] = useState<boolean>(
    selectedQuestionId === "" ? true : false
  );
  const [activeUserQuestionId, setActiveUserQuestionId] = useState<
    string | undefined
  >(userCredentials?.questions?.[0]?.id);
  const userQuestionId = userCredentials?.questions?.[0]?.id;
  const allPendingActions = [
    ...userCredentialsPendingActions?.require,
    ...userCredentialsPendingActions?.optional,
  ];

  const [resetingSQAStatus, setResetingSQAStatus] = useState<FormActionStatus>(
    FormActionStatus.NO_ACTION
  );
  const [settingTemporaryPasswordStatus, setSettingTemporaryPasswordStatus] =
    useState<FormActionStatus>(FormActionStatus.NO_ACTION);
  const [updatingSQAStatus, setUpdatingSQAStatus] = useState<FormActionStatus>(
    FormActionStatus.NO_ACTION
  );
  const [blokUnblockStatus, setBlockUnblockStatus] = useState<{
    fraud: FormActionStatus;
    bruteForce: FormActionStatus;
  }>({
    fraud: FormActionStatus.NO_ACTION,
    bruteForce: FormActionStatus.NO_ACTION,
  });

  const [temporaryPassword, setTemporaryPassword] = useState<string>("");

  const resetSecurityQuestion = async (
    credentialId: string,
    resetForm: any
  ) => {
    setResetingSQAStatus(FormActionStatus.IN_PROGRESS);

    try {
      await userCredentialsApi.resetSecurityQuestion(credentialId);
      setUserCredentialsPendingActions((prevVal: PendingActions) => {
        return prevVal.require.includes(CredentialsStatusActions.sqaMissing)
          ? prevVal
          : {
              ...prevVal,
              require: [
                ...prevVal.require,
                CredentialsStatusActions.sqaMissing,
              ],
            };
      });
      resetForm({
        values: {
          securityQuestion: "",
          securityAnswer: "",
        },
      });
      setUserCredentials((prevVal) => {
        delete prevVal?.questions;
        return prevVal;
      });
      setShowAnswer(true);
      setActiveUserQuestionId(undefined);
      setResetingSQAStatus(FormActionStatus.DONE);
      setTimeout((e) => {
        setResetingSQAStatus(FormActionStatus.NO_ACTION);
      }, 2000);
      await handleTabDataReload();
    } catch (error) {
      console.error("Reset security question error: ", error);
      setResetingSQAStatus(FormActionStatus.ERROR);
      setTimeout(() => {
        setResetingSQAStatus(FormActionStatus.NO_ACTION);
      }, 2000);
    }
  };

  const unblockUser = async (credentialId: string, type: BlockReasonTypes) => {
    setBlockUnblockStatus((prevStatus) => ({
      ...prevStatus,
      [BlockReasonTypesProperties[type]]: FormActionStatus.IN_PROGRESS,
    }));
    try {
      await userCredentialsApi.unblockUserCredentials(credentialId, type);
      const response = await userCredentialsApi.getCredentialsById(
        credentialId
      );

      setUserCredentials(
        (prevUserCredentials) =>
          prevUserCredentials && {
            ...prevUserCredentials,
            blocks: response.blocks,
          }
      );
      setBlockUnblockStatus((prevStatus) => ({
        ...prevStatus,
        [BlockReasonTypesProperties[type]]: FormActionStatus.DONE,
      }));
      setTimeout(() => {
        setBlockUnblockStatus((prevStatus) => ({
          ...prevStatus,
          [BlockReasonTypesProperties[type]]: FormActionStatus.NO_ACTION,
        }));
      }, 1000);

      await handleTabDataReload();
    } catch (error) {
      console.error("Unblock user error: ", error);
    }
  };

  const blockUser = async (credentialId: string, type: BlockReasonTypes) => {
    setBlockUnblockStatus((prevStatus) => ({
      ...prevStatus,
      [BlockReasonTypesProperties[type]]: FormActionStatus.IN_PROGRESS,
    }));
    try {
      await userCredentialsApi.blockUserCredentials(credentialId, type);
      const response = await userCredentialsApi.getCredentialsById(
        credentialId
      );
      setUserCredentials(
        (prevUserCredentials) =>
          prevUserCredentials && {
            ...prevUserCredentials,
            blocks: response.blocks,
          }
      );
      setBlockUnblockStatus((prevStatus) => ({
        ...prevStatus,
        [BlockReasonTypesProperties[type]]: FormActionStatus.DONE,
      }));
      setTimeout(() => {
        setBlockUnblockStatus((prevStatus) => ({
          ...prevStatus,
          [BlockReasonTypesProperties[type]]: FormActionStatus.NO_ACTION,
        }));
      }, 1000);
      await handleTabDataReload();
    } catch (error) {
      console.error("Block user error: ", error);
      setBlockUnblockStatus((prevStatus) => ({
        ...prevStatus,
        [BlockReasonTypesProperties[type]]: FormActionStatus.ERROR,
      }));
      setTimeout(() => {
        setBlockUnblockStatus((prevStatus) => ({
          ...prevStatus,
          [BlockReasonTypesProperties[type]]: FormActionStatus.NO_ACTION,
        }));
      }, 2000);
    }
  };

  const generateTemporaryPassword = async (credentialId: string) => {
    setSettingTemporaryPasswordStatus(FormActionStatus.IN_PROGRESS);
    try {
      const response = await userCredentialsApi.setTemporaryPassword(
        credentialId
      );
      setTemporaryPassword(response.password);
      setUserCredentialsPendingActions((prevVal: PendingActions) => {
        //passwordForceReset added to array because it will be re-added below
        const actionsToRemove = [
          CredentialsStatusActions.passwordMissing,
          CredentialsStatusActions.passwordForceReset,
        ];

        return {
          ...prevVal,
          require: [
            ...prevVal.require.filter(
              (action) => !actionsToRemove.includes(action)
            ),
            CredentialsStatusActions.passwordForceReset,
          ],
        };
      });
      setSettingTemporaryPasswordStatus(FormActionStatus.DONE);
      setTimeout(() => {
        setSettingTemporaryPasswordStatus(FormActionStatus.NO_ACTION);
      }, 1000);
      setTimeout(() => {
        setTemporaryPassword("");
      }, 20000);
      await handleTabDataReload();
    } catch (error) {
      console.error("Generate temporary password error: ", error);
      setSettingTemporaryPasswordStatus(FormActionStatus.ERROR);
      setTimeout(() => {
        setSettingTemporaryPasswordStatus(FormActionStatus.NO_ACTION);
      }, 2000);
    }
  };

  const updateSQA = async (
    updateCredentialsRequest: UpdateUserCredentialsRequest
  ) => {
    setUpdatingSQAStatus(FormActionStatus.IN_PROGRESS);
    try {
      const userCredentialsResponse =
        await userCredentialsApi.updateUserCredentials(
          updateCredentialsRequest,
          ["answer"]
        );
      setUserCredentialsPendingActions((prevVal: PendingActions) => {
        const actionsToRemove = [
          CredentialsStatusActions.sqaMissing,
          CredentialsStatusActions.sqaIncomplete,
        ];
        return {
          optional: [
            ...prevVal.optional.filter(
              (action) => !actionsToRemove.includes(action)
            ),
          ],
          require: [
            ...prevVal.require.filter(
              (action) => !actionsToRemove.includes(action)
            ),
          ],
        };
      });
      setUserCredentials(
        (prevVal) =>
          prevVal && {
            ...prevVal,
            questions: userCredentialsResponse?.questions,
          }
      );
      setActiveUserQuestionId(userCredentialsResponse?.questions?.[0]?.id);
      setErrorOutlines({});
      setAlertMessageProps(null);
      setUpdatingSQAStatus(FormActionStatus.DONE);
      setTimeout(() => {
        setUpdatingSQAStatus(FormActionStatus.NO_ACTION);
      }, 1000);
    } catch (error) {
      console.error("Update SQA error: ", error);
      setUpdatingSQAStatus(FormActionStatus.ERROR);
      setTimeout(() => {
        setUpdatingSQAStatus(FormActionStatus.NO_ACTION);
      }, 2000);
      if (error instanceof ValidationError) {
        return error.getValidationErrorResponse();
      }

      if(error instanceof NetworkError){
        setErrorOutlines((prevVal:any) => ({
          ...prevVal,
          securityAnswer: true
        }));
        setAlertMessageProps({
          type: "error",
          text: "Invalid characters or combination of characters have been used."
        });
        return;
      }
    }
  };

  const handleSQAUpdate = async (
    values: any,
    setErrors: any
  ): Promise<void> => {
    if (userCredentials) {
      const response = await updateSQA({
        id: userCredentials.id,
        questions: [
          {
            id: activeUserQuestionId,
            questionId:
              values.securityQuestion !== freeFormQuestionId
                ? values.securityQuestion
                : undefined,
            answer: stringToHex(values.securityAnswer),
          },
        ],
      });

      if (values.securityQuestion !== freeFormQuestionId) {
        allQuestions = allQuestions.filter(
          (question) => question.id !== freeFormQuestionId
        );
      }

      response?.validationErrors &&
        setErrors({
          securityAnswer: response?.validationErrors.join("<br/>"),
        });

      await handleTabDataReload();
    }
  };

  return (
    <Formik
      initialValues={{
        securityQuestion: selectedQuestionId,
        securityAnswer: activeAnswer,
      }}
      validationSchema={sqaFormValidationSchema}
      onSubmit={(values, actions) => {
        handleSQAUpdate(values, actions.setErrors);
      }}
    >
      {({ errors, values, handleChange, resetForm }) => (
        <Form>
          <Grid container columnSpacing={8} rowSpacing={1}>
            <Grid item xs={12} sm={6}>
              <h2>Status</h2>
              <>
                <div className={classes.statusSectionHolder}>
                  {allPendingActions?.length ? (
                    allPendingActions.map(
                      (
                        pendingAction: CredentialsStatusActions,
                        index: number
                      ) => (
                        <p key={index}>
                          {credentialsStatusMap.get(pendingAction)}
                        </p>
                      )
                    )
                  ) : (
                    <p>No pending actions</p>
                  )}
                </div>
                <PermissionCheck
                  userPermissions={userPermissions}
                  permissionToCheck={UserPermissions.SQA_VIEW}
                >
                  {!!allQuestions?.length && (
                    <div className={classes.sqaHolder}>
                      <label htmlFor="securityQuestion">
                        Security Question
                      </label>
                      <Field
                        fullWidth
                        component={Select}
                        data-testid="select-question-option"
                        value={values.securityQuestion}
                        onChange={(e: any) => {
                          setSelectedQuestionId(e.target.value);
                          handleChange(e);
                        }}
                        inputProps={{
                          id: "securityQuestion",
                          name: "securityQuestion",
                        }}
                      >
                        {allQuestions.map((question) => (
                          <MenuItem key={question.id} value={question.id} data-testid="predefined-question-option">
                            {question.question}
                          </MenuItem>
                        ))}
                      </Field>
                      {errors.securityQuestion && (
                        <div>
                          <div className={classes.fieldErrorsHolder}>
                            <i className="t-error"></i>
                            {parseHtml(errors.securityQuestion)}
                          </div>
                        </div>
                      )}
                      <div className={classes.securityAnswerHolder}>
                        <label htmlFor="securityAnswer">Security Answer</label>
                        <SecurityAnswerTooltip />
                        <PermissionCheck
                          userPermissions={userPermissions}
                          permissionToCheck={UserPermissions.SQA_EDIT}
                        >
                          {!!selectedQuestionId && !!userQuestionId && (
                            <Link
                              tabIndex={0}
                              className={classes.showAnswer}
                              onKeyDown={(e) =>
                                e.key === "Enter" && setShowAnswer(!showAnswer)
                              }
                              onClick={(e) => setShowAnswer(!showAnswer)}
                            >
                              {showAnswer ? `Hide` : `Show`}
                            </Link>
                          )}
                        </PermissionCheck>
                        <Field
                          className={errorOutlines["securityAnswer"] ? "errorOutline" : ""}
                          component={TextField}
                          name="securityAnswer"
                          id="securityAnswer"
                          variant="outlined"
                          fullWidth
                          type={showAnswer ? "text" : "password"}
                          value={values.securityAnswer}
                          onChange={(e: any) => {
                            setActiveAnswer(e.target.value);
                            handleChange(e);
                          }}
                        />
                        {errors.securityAnswer && (
                          <div>
                            <div className={classes.fieldErrorsHolder}>
                              <i className="t-error"></i>
                              {parseHtml(errors.securityAnswer)}
                            </div>
                          </div>
                        )}
                      </div>
                      <PermissionCheck
                        userPermissions={userPermissions}
                        permissionToCheck={UserPermissions.SQA_EDIT}
                      >
                        <Button variant="outlined" type="submit">
                          Update Security Q&A
                        </Button>
                        <FormActionStatusIcon
                          brand={brand}
                          status={updatingSQAStatus}
                        />
                      </PermissionCheck>
                    </div>
                  )}
                </PermissionCheck>
              </>
            </Grid>
            <Grid item xs={12} sm={6}>
              <h2>Actions</h2>
              <div className={classes.actionsHolder}>
                <PermissionCheck
                  userPermissions={userPermissions}
                  permissionToCheck={
                    UserPermissions.GENERATE_TEMPORARY_PASSWORD_EDIT
                  }
                >
                  <div>
                    <div className="icon-text-holder">
                      <i
                        className={`${classes.generateTemporaryPassword} t-reset-password`}
                        style={{ color: brand?.actionItemColor || "#006C7D" }}
                      ></i>
                      <Link
                        tabIndex={0}
                        onKeyDown={(e) =>
                          e.key === "Enter" &&
                          generateTemporaryPassword(userCredentials.id)
                        }
                        onClick={(e) =>
                          generateTemporaryPassword(userCredentials.id)
                        }
                      >
                        Generate Temporary Password
                      </Link>
                      <FormActionStatusIcon
                        brand={brand}
                        status={settingTemporaryPasswordStatus}
                      />
                    </div>
                    <div className={classes.temporaryPasswordHolder}>
                      <label htmlFor="temporaryPassword">
                        Temporary password
                      </label>
                      <div className={classes.temporaryPasswordWithCopyIcon}>
                        <TextField
                          variant="outlined"
                          id="temporaryPassword"
                          fullWidth
                          contentEditable={false}
                          className={classes.temporaryPasswordTextField}
                          value={temporaryPassword}
                        />
                        {!!temporaryPassword && (
                          <i
                            className={`${classes.copyTemporaryPassword} t-clipboard`}
                            onClick={() => {
                              navigator.clipboard.writeText(temporaryPassword);
                            }}
                          ></i>
                        )}
                      </div>
                    </div>
                  </div>
                </PermissionCheck>
                <div className={classes.forceUpdatesHolder}>
                  <PermissionCheck
                    userPermissions={userPermissions}
                    permissionToCheck={UserPermissions.FORCE_SQA_UPDATE_EDIT}
                  >
                    <p>
                      <Link
                        tabIndex={0}
                        onKeyDown={(e) =>
                          e.key === "Enter" &&
                          resetSecurityQuestion(userCredentials.id, resetForm)
                        }
                        onClick={(e) =>
                          resetSecurityQuestion(userCredentials.id, resetForm)
                        }
                        className="icon-text-holder"
                      >
                        <i className="t-telemedicine"></i>
                        <span>Force Security Question Update</span>
                        <FormActionStatusIcon
                          brand={brand}
                          status={resetingSQAStatus}
                        />
                      </Link>
                    </p>
                  </PermissionCheck>
                </div>
                <div className={classes.blockUnblockHolder}>
                  <PermissionCheck
                    userPermissions={userPermissions}
                    permissionToCheck={UserPermissions.BRUTE_FORCE_BLOCK_EDIT}
                  >
                    {!!userCredentials?.blocks?.filter(
                      (credentialBlock: CredentialBlock) =>
                        credentialBlock.type === BlockReasonTypes.BRUTE_FORCE
                    )?.length && (
                      <p>
                        <Link
                          tabIndex={0}
                          onKeyDown={(e) =>
                            e.key === "Enter" &&
                            unblockUser(
                              userCredentials.id,
                              BlockReasonTypes.BRUTE_FORCE
                            )
                          }
                          onClick={(e) =>
                            unblockUser(
                              userCredentials.id,
                              BlockReasonTypes.BRUTE_FORCE
                            )
                          }
                          className="icon-text-holder"
                        >
                          <i className="t-check-circle"></i>
                          <span>Unblock for too many attempts</span>
                        </Link>
                      </p>
                    )}
                  </PermissionCheck>
                  <PermissionCheck
                    userPermissions={userPermissions}
                    permissionToCheck={UserPermissions.FRAUD_BLOCK_EDIT}
                  >
                    <p>
                      {userCredentials?.blocks?.filter(
                        (credentialBlock: CredentialBlock) =>
                          credentialBlock.type === BlockReasonTypes.FRAUD
                      )?.length ? (
                        <Link
                          tabIndex={0}
                          onKeyDown={(e) =>
                            e.key === "Enter" &&
                            unblockUser(
                              userCredentials.id,
                              BlockReasonTypes.FRAUD
                            )
                          }
                          onClick={(e) =>
                            unblockUser(
                              userCredentials.id,
                              BlockReasonTypes.FRAUD
                            )
                          }
                          className="icon-text-holder"
                        >
                          <i className="t-check-circle"></i>
                          <span>Unblock User for Fraud</span>
                        </Link>
                      ) : (
                        <Link
                          tabIndex={0}
                          onKeyDown={(e) =>
                            e.key === "Enter" &&
                            blockUser(
                              userCredentials.id,
                              BlockReasonTypes.FRAUD
                            )
                          }
                          onClick={(e) =>
                            blockUser(
                              userCredentials.id,
                              BlockReasonTypes.FRAUD
                            )
                          }
                          className="icon-text-holder"
                        >
                          <i className="t-error"></i>
                          <span>Block User for Fraud</span>
                        </Link>
                      )}
                      <FormActionStatusIcon
                        brand={brand}
                        status={blokUnblockStatus.fraud}
                      />
                    </p>
                  </PermissionCheck>
                </div>
              </div>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default LoginAndSecurity;
